import { Alert, Box, Button, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { FirebaseError } from 'firebase/app';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '@/services';

interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const authErrorMessages: Record<string, string> = {
    'auth/invalid-email': t('login.errors.invalidEmail'),
    'auth/invalid-credential': t('login.errors.invalidCredential'),
    'auth/user-disabled': t('login.errors.userDisabled'),
    'auth/too-many-requests': t('login.errors.tooManyRequests'),
  };

  const form = useForm<LoginFormValues>({
    initialValues: { email: '', password: '' },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : t('login.errors.validEmail')),
      password: (value) => (value.length > 0 ? null : t('login.errors.enterPassword')),
    },
  });

  const handleSubmit = async (values: LoginFormValues) => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
      const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const code = error instanceof FirebaseError ? error.code : undefined;
      setSubmitError((code && authErrorMessages[code]) ?? t('login.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      mih="100vh"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '24px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Oversized ghost numeral, purely decorative — reinforces the
          "manifest / dispatch sheet" motif without competing with content. */}
      <Text
        aria-hidden="true"
        ff="heading"
        fw={900}
        style={{
          position: 'absolute',
          top: -40,
          right: -30,
          fontSize: '16.25rem',
          lineHeight: 1,
          color: 'var(--mantine-color-ink-3)',
          userSelect: 'none',
          zIndex: 0,
        }}
      >
        01
      </Text>

      <Box
        style={{ position: 'relative', zIndex: 1, maxWidth: 380, width: '100%', margin: '0 auto' }}
      >
        <Text
          c="signal.7"
          ff="monospace"
          fw={700}
          mb={4}
          size="xs"
          style={{ letterSpacing: '0.28em' }}
        >
          {t('login.eyebrow')}
        </Text>
        <Title lh={0.95} mb={6} order={1} style={{ fontSize: '2.75rem' }}>
          ROUTE
          <br />
          PLANNER
        </Title>
        <Text c="ink.7" mb="xl" size="sm">
          {t('login.subtitle')}
        </Text>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {submitError && (
              <Alert
                color="signal"
                styles={{ root: { border: '1px solid var(--mantine-color-signal-6)' } }}
                variant="outline"
              >
                {submitError}
              </Alert>
            )}
            <TextInput
              autoComplete="email"
              label={t('login.email')}
              labelProps={{
                style: { letterSpacing: '0.1em', fontSize: '0.6875rem', fontWeight: 700 },
              }}
              placeholder="you@example.com"
              styles={{
                input: { backgroundColor: '#ffffff', borderColor: 'var(--mantine-color-ink-3)' },
              }}
              {...form.getInputProps('email')}
            />
            <PasswordInput
              autoComplete="current-password"
              label={t('login.password')}
              labelProps={{
                style: { letterSpacing: '0.1em', fontSize: '0.6875rem', fontWeight: 700 },
              }}
              placeholder="••••••••"
              styles={{
                input: { backgroundColor: '#ffffff', borderColor: 'var(--mantine-color-ink-3)' },
              }}
              {...form.getInputProps('password')}
            />
            <Button
              fullWidth
              loading={submitting}
              mt="sm"
              size="md"
              style={{ letterSpacing: '0.08em', fontWeight: 700 }}
              type="submit"
            >
              {t('login.signIn')}
            </Button>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};
